import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiActivity, FiArrowLeft, FiBell, FiDollarSign, FiFileMinus, FiKey, FiLock, FiSettings } from 'react-icons/fi';
import AppSettings from 'components/app/settings/AppSettings';
import ApiKeysManager from 'components/app/settings/ApiKeysManager';
import MonitoringLogs from 'components/app/settings/MonitoringLogs';
import OrphanAttachments from 'components/app/settings/orphan-attachments/OrphanAttachments';
import PaymentPlanRates from 'components/app/settings/payment-plan-rates/PaymentPlanRates';
import RemindersOverview from 'components/app/settings/reminders/RemindersOverview';

const TABS = [
  { key: 'settings', label: 'Configuration', icon: FiSettings, hint: 'Every key this deployment reads at runtime' },
  { key: 'reminders', label: 'Reminders',    icon: FiBell,     hint: 'Scheduled emails that remind people of work waiting for them' },
  { key: 'api_keys', label: 'API Keys',      icon: FiKey,      hint: 'Credentials issued to outside callers' },
  { key: 'logs',     label: 'Prod Logs',     icon: FiActivity, hint: 'Live application logs' },
  { key: 'orphans',  label: 'Orphan Files',  icon: FiFileMinus, hint: 'Attachment rows whose stored file is gone' },
  { key: 'payment_rates', label: 'Payment Rates', icon: FiDollarSign, hint: 'USD exchange rates for non-USD payment plan milestones' },
];

const AppSettingsPage = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  // In the URL so a tab is linkable and survives a refresh — the same reason
  // the group and search live there.
  const active = TABS.some((t) => t.key === params.get('tab')) ? params.get('tab') : 'settings';

  const selectTab = (key) => setParams((prev) => {
    const next = new URLSearchParams(prev);
    // Group/search/filter belong to the Configuration tab only; carrying them
    // across would leave a filter applied on a tab that cannot show it.
    ['group', 'sub', 'q', 'filter', 'reminder'].forEach((p) => next.delete(p));
    if (key === 'settings') next.delete('tab'); else next.set('tab', key);
    return next;
  }, { replace: true });

  // Reminders tab: which reminder is open (?reminder=<key>), linkable like the rest.
  const selectReminder = (key) => setParams((prev) => {
    const next = new URLSearchParams(prev);
    next.set('reminder', key);
    return next;
  }, { replace: true });

  return (
    <div className="tw-min-h-screen tw-bg-slate-50">

      <header className="tw-bg-white tw-border-b tw-border-slate-200">
        <div className="tw-mx-auto tw-w-full tw-px-3 sm:tw-px-5 tw-pt-4" style={{ maxWidth: 1440 }}>

          <div className="tw-flex tw-items-start tw-justify-between tw-gap-4 tw-flex-wrap">
            <div className="tw-flex tw-items-center tw-gap-3 tw-min-w-0">
              <button
                type="button"
                onClick={() => navigate(-1)}
                aria-label="Back"
                title="Back"
                className="tw-w-8 tw-h-8 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white hover:tw-bg-slate-50 tw-flex tw-items-center tw-justify-center tw-cursor-pointer tw-transition-colors tw-shrink-0"
              >
                <FiArrowLeft className="tw-text-slate-500" size={14} />
              </button>

              <div className="tw-w-9 tw-h-9 tw-rounded-lg tw-bg-primary-50 tw-flex tw-items-center tw-justify-center tw-shrink-0">
                <FiSettings className="tw-text-primary-600" size={17} />
              </div>

              <div className="tw-min-w-0">
                <h1 className="tw-text-[17px] tw-font-semibold tw-text-slate-900 tw-mb-0 tw-leading-tight">App Settings</h1>
                <p className="tw-text-[12.5px] tw-text-slate-500 tw-mb-0">
                  Runtime configuration for this deployment — changes apply without a redeploy
                </p>
              </div>
            </div>

            <div className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-1 tw-rounded-lg tw-bg-slate-50 tw-border tw-border-slate-200 tw-shrink-0">
              <FiLock className="tw-text-slate-400" size={12} />
              <span className="tw-text-[12px] tw-font-medium tw-text-slate-600">Sensitive values are masked</span>
            </div>
          </div>

          <nav className="tw-flex tw-items-center tw-gap-4 sm:tw-gap-6 tw-mt-4 tw-overflow-x-auto tw-overscroll-x-contain tw-no-scrollbar">
            {TABS.map(({ key, label, icon: Icon, hint }) => {
              const current = active === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => selectTab(key)}
                  title={hint}
                  aria-current={current ? 'page' : undefined}
                  className={[
                    'tw-flex tw-shrink-0 tw-items-center tw-gap-1.5 tw-pb-2.5 tw-px-0 tw-text-[13px] tw-font-semibold tw-whitespace-nowrap',
                    'tw-bg-transparent tw-border-0 tw-border-b-2 tw-border-solid tw-cursor-pointer tw-transition-colors',
                    current
                      ? 'tw-text-primary-700 tw-border-primary-600'
                      : 'tw-text-slate-500 hover:tw-text-slate-800 tw-border-transparent',
                  ].join(' ')}
                  style={{ marginBottom: -1 }}
                >
                  <Icon size={13} /> {label}
                </button>
              );
            })}
          </nav>
        </div>
      </header>

      {active === 'settings' && <AppSettings />}

      {/* The other tabs bring their own padding but no measure — wrapping them
          keeps every tab at the same width on a wide monitor. */}
      {active !== 'settings' && (
        <div className="tw-mx-auto tw-w-full" style={{ maxWidth: 1440 }}>
          {active === 'reminders' && (
            <div className="tw-px-3 sm:tw-px-5 tw-py-4">
              <RemindersOverview selectedKey={params.get('reminder')} onSelect={selectReminder} />
            </div>
          )}
          {active === 'api_keys' && <ApiKeysManager />}
          {active === 'logs' && <MonitoringLogs />}
          {active === 'orphans' && <OrphanAttachments />}
          {active === 'payment_rates' && <PaymentPlanRates />}
        </div>
      )}
    </div>
  );
};

export default AppSettingsPage;
